import { useState, useEffect, useRef } from 'react';

export default function OrderingProcess() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const sectionRef = useRef<HTMLElement>(null);

  // Scroll-based automation
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate scroll progress within the section
      // Start when section enters viewport, end when it exits
      const sectionTop = rect.top;
      const sectionBottom = rect.bottom;
      const sectionHeight = rect.height;

      // Check if section is in viewport
      if (sectionBottom < 0 || sectionTop > viewportHeight) {
        return; // Section not in view
      }

      // Calculate progress (0 to 1)
      // Progress starts when section top is at 80% viewport
      // Progress ends when section bottom is at 20% viewport
      const startTrigger = viewportHeight * 0.8;
      const endTrigger = viewportHeight * 0.2;

      let progress = 0;
      
      if (sectionTop <= startTrigger) {
        // Section is entering/in view
        const travelled = startTrigger - sectionTop;
        const totalDistance = sectionHeight + (startTrigger - endTrigger);
        progress = Math.min(travelled / totalDistance, 1);
      }

      // Map progress to steps (0 → 0, 0.33 → 1, 0.66 → 2, 1 → 3)
      let newStep = 0;
      if (progress >= 0.75) {
        newStep = 3;
      } else if (progress >= 0.5) {
        newStep = 2;
      } else if (progress >= 0.25) {
        newStep = 1;
      } else {
        newStep = 0;
      }

      setActiveStep(newStep);
    };

    // Initial check
    handleScroll();

    // Attach scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const steps = [
    {
      number: '01',
      icon: 'ri-file-list-3-line',
      title: 'Share Requirement',
      description: 'Submit enquiry with product details',
    },
    {
      number: '02',
      icon: 'ri-price-tag-line',
      title: 'Receive Bulk Quote',
      description: 'Get pricing within 24 hours',
    },
    {
      number: '03',
      icon: 'ri-checkbox-circle-line',
      title: 'Confirm Order',
      description: 'Approve quote and make payment',
    },
    {
      number: '04',
      icon: 'ri-map-pin-line',
      title: 'Track Dispatch',
      description: 'Receive tracking details instantly',
    },
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-12 lg:mt-16 bg-gradient-to-b from-white to-gray-50 overflow-hidden"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header - Center Aligned */}
        <div className="text-center mb-12 lg:mb-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
            Simple B2B Ordering Process
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            From enquiry to delivery, we make bulk ordering effortless
          </p>
        </div>

        {/* Mobile: Vertical Flow with Active Segment Line */}
        <div className="md:hidden relative pb-6">
          {/* Base Connector Line (Muted, Always Visible) */}
          <div 
            className="absolute left-6 top-6 w-1 bg-gray-300 rounded-full"
            style={{ height: 'calc(100% - 112px)' }}
          ></div>

          {/* Active Segment Line (Bold Brand Color) */}
          <div 
            className="absolute left-6 top-6 w-1 bg-teal-600 rounded-full transition-all duration-500 ease-out"
            style={{ 
              height: activeStep === 0 ? '0%' : 
                      activeStep === 1 ? 'calc(25% + 12px)' : 
                      activeStep === 2 ? 'calc(50% + 24px)' : 
                      'calc(75% + 36px)'
            }}
          ></div>

          {/* Steps */}
          <div className="space-y-6 mb-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative pl-16 step-entrance"
                style={{ 
                  animationDelay: `${index * 100}ms`,
                }}
                onClick={() => setActiveStep(index)}
              >
                {/* Step Number Badge with Premium Ring Highlight */}
                <div
                  className={`absolute left-0 w-12 h-12 flex items-center justify-center rounded-full font-bold text-lg z-10 transition-all duration-450 cursor-pointer ${
                    activeStep === index 
                      ? 'bg-teal-600 text-white shadow-lg ring-4 ring-teal-200 ring-offset-2' 
                      : activeStep > index
                      ? 'bg-teal-700 text-white'
                      : 'bg-gray-400 text-white'
                  }`}
                  style={{
                    transform: activeStep === index ? 'scale(1.08)' : 'scale(1)',
                  }}
                >
                  {step.number}
                </div>

                {/* Step Card */}
                <div className={`bg-white rounded-xl p-4 border-2 shadow-sm card-interactive transition-all duration-300 cursor-pointer ${
                  activeStep === index ? 'border-teal-500 shadow-lg' : 'border-gray-200'
                }`}>
                  <div className="flex items-start space-x-3">
                    {/* Icon with micro-motion */}
                    <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      activeStep === index ? 'text-teal-600' : 'text-gray-600'
                    }`}
                    style={{
                      transform: activeStep === index ? 'scale(1.03)' : 'scale(1)',
                    }}
                    >
                      <i className={`${step.icon} text-2xl`}></i>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-lg font-bold mb-1 text-gray-900">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Info Pill */}
          <div className="bg-amber-50 border border-amber-200 rounded-full px-4 py-2.5 text-center badge-entrance">
            <p className="text-xs text-gray-700">
              <strong className="text-amber-700">B2B Only:</strong> Min order value may apply
            </p>
          </div>
        </div>

        {/* Desktop: Horizontal Timeline with Active Segment */}
        <div className="hidden md:block">
          <div className="relative pb-8">
            {/* Base Connector Line (Muted, Always Visible) */}
            <div className="absolute top-10 left-10 right-10 h-1 bg-gray-300 rounded-full"></div>

            {/* Active Segment Line (Bold Brand Color) */}
            <div 
              className="absolute top-10 left-10 h-1 bg-teal-600 rounded-full transition-all duration-500 ease-out"
              style={{ 
                width: activeStep === 0 ? '0%' : 
                        activeStep === 1 ? 'calc(25% + 40px)' : 
                        activeStep === 2 ? 'calc(50% + 80px)' : 
                        'calc(75% + 120px)'
              }}
            ></div>

            {/* Steps Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className="relative flex flex-col items-center text-center step-entrance"
                  style={{ 
                    animationDelay: `${index * 150}ms`,
                  }}
                  onClick={() => setActiveStep(index)}
                >
                  {/* Number Badge with Premium Ring Highlight */}
                  <div
                    className={`relative z-10 w-20 h-20 flex items-center justify-center rounded-full text-2xl font-bold shadow-lg mb-4 transition-all duration-450 cursor-pointer ${
                      activeStep === index
                        ? 'bg-teal-600 text-white shadow-2xl ring-4 ring-teal-200 ring-offset-4'
                        : activeStep > index
                        ? 'bg-teal-700 text-white'
                        : 'bg-gray-400 text-white'
                    }`}
                    style={{
                      transform: activeStep === index ? 'scale(1.1)' : 'scale(1)',
                    }}
                  >
                    {step.number}
                  </div>

                  {/* Icon with micro-motion */}
                  <div className={`w-12 h-12 flex items-center justify-center mb-3 transition-all duration-300 ${
                    activeStep === index ? 'text-teal-600' : 'text-gray-600'
                  }`}
                  style={{
                    transform: activeStep === index ? 'scale(1.03)' : 'scale(1)',
                  }}
                  >
                    <i className={`${step.icon} text-4xl`}></i>
                  </div>

                  {/* Content */}
                  <div className={`space-y-2 cursor-pointer card-interactive-desktop transition-all duration-300 ${
                    activeStep === index ? 'text-scale' : ''
                  }`}>
                    <h3 className="text-xl font-bold text-gray-900">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600 max-w-xs mx-auto">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Info Pill - Desktop */}
            <div className="max-w-md mx-auto">
              <div className="bg-amber-50 border border-amber-200 rounded-full px-6 py-3 text-center badge-entrance">
                <p className="text-sm text-gray-700">
                  <strong className="text-amber-700">B2B Only:</strong> Minimum order value may apply based on category
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Premium CSS-Only Animations */}
      <style>{`
        /* ===== ENTRANCE ANIMATIONS (VISIBLE BY DEFAULT) ===== */
        
        .title-entrance {
          animation: titleFadeIn 0.8s ease-out forwards;
          opacity: 1;
        }
        
        .subtitle-entrance {
          animation: subtitleFadeIn 0.8s ease-out 0.2s forwards;
          opacity: 1;
        }
        
        .step-entrance {
          animation: stepSlideIn 0.6s ease-out forwards;
          opacity: 1;
        }
        
        .badge-entrance {
          animation: badgeFadeIn 0.6s ease-out 0.8s forwards;
          opacity: 1;
        }

        @keyframes titleFadeIn {
          from {
            transform: translateY(-10px);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes subtitleFadeIn {
          from {
            transform: translateY(-8px);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes stepSlideIn {
          from {
            transform: translateY(10px);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes badgeFadeIn {
          from {
            transform: scale(0.95);
          }
          to {
            transform: scale(1);
          }
        }

        /* ===== CARD MICRO-INTERACTIONS ===== */
        
        .card-interactive {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .card-interactive:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12);
          border-color: #14b8a6;
        }

        .card-interactive:active {
          transform: translateY(-2px);
        }

        .card-interactive-desktop {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .card-interactive-desktop:hover {
          transform: translateY(-4px);
        }

        /* ===== TEXT SCALE ON ACTIVE ===== */
        
        .text-scale {
          animation: textEmphasis 0.3s ease-out;
        }

        @keyframes textEmphasis {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.03);
          }
          100% {
            transform: scale(1);
          }
        }

        /* ===== PERFORMANCE OPTIMIZATION ===== */
        
        * {
          -webkit-tap-highlight-color: transparent;
        }
        
        .card-interactive,
        .card-interactive-desktop {
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
      `}</style>
    </section>
  );
}
